// PRE RMA — « Valeurs de référence » du module Intrants (section Paludisme).
//
// But : donner au Chef CSB un REPÈRE VISUEL à côté de la colonne « Utilisée (3) » : combien de TDR / d'ACT / d'artésunate / de SP
// les consultations (RCE) et la CPN saisies ce mois-ci laissent attendre. Ces valeurs :
//   - sont calculées AUTOMATIQUEMENT à partir des autres modules (jamais saisies) ;
//   - ne sont JAMAIS utilisées dans les formules de stock (stock.ts, modules.ts n'importent pas ce fichier).
// Fonctions pures : mêmes chiffres hors connexion qu'en ligne.
import { normaliserLignes } from './rma.ts';
import type { ConsultationRow } from './rma.ts';
import { tranchePaluSure } from './tranches.ts';
import { RULES } from './rules.config.ts';

export interface ReferencesPalu {
  /** Nombre total de TDR effectués (module Consultation : cas de fièvre avec un résultat de TDR). */
  tdr: number;
  /** [0-11 mois] avec TDR+ traités par ACT. */
  act0_11mois: number;
  /** [1-5 ans] avec TDR+ traités par ACT. */
  act1_5ans: number;
  /** [6-13 ans] avec TDR+ traités par ACT. */
  act6_13ans: number;
  /** [14-24 ans] + [25 ans et plus] avec TDR+ traités par ACT. */
  act14ansEtPlus: number;
  /** Cas sévères (TDR+) traités par Artésunate injectable. */
  artesunateInjectable: number;
  /** Femmes enceintes ayant reçu TPI1 + TPI2 + TPI3 + TPI4 et plus (module CPN). */
  sulfadoxinePyrimethamine: number;
}

export const REFERENCES_PALU_VIDES: ReferencesPalu = {
  tdr: 0, act0_11mois: 0, act1_5ans: 0, act6_13ans: 0, act14ansEtPlus: 0, artesunateInjectable: 0, sulfadoxinePyrimethamine: 0,
};

/** Doses de TPI telles que calculées par le RMA CPN (calculerRmaCpn().tpiEtMid). */
export interface TpiCpn { tpi1: number; tpi2: number; tpi3: number; tpi4EtPlus: number }

/**
 * Correspondance « produit du catalogue Paludisme (STOCK_PROGRAMMES) → valeur de référence ».
 * Par NOM de produit (et non par position) : l'ordre du catalogue peut évoluer sans décaler les références.
 * Les produits absents de cette table (AL, Primaquine, MID…) n'ont pas de valeur de référence.
 */
export const REFERENCE_PAR_PRODUIT_PALU: Readonly<Record<string, keyof ReferencesPalu>> = {
  'RDT': 'tdr',
  'ASAQ 2-11 mois': 'act0_11mois',
  'ASAQ 1-5 ans': 'act1_5ans',
  'ASAQ 6-13 ans': 'act6_13ans',
  'ASAQ 14 ans +': 'act14ansEtPlus',
  'Artesunate injectable': 'artesunateInjectable',
  'Sulfadoxine-Pyriméthamine femme enceinte (cp)': 'sulfadoxinePyrimethamine',
};

const entier = (v: unknown): number => { const x = Number(v); return Number.isFinite(x) && x > 0 ? Math.floor(x) : 0; };
const TRANCHES = RULES.tranches.palu.map((b) => b.label);
const [T_0_11, T_1_5, T_6_13, T_14_24, T_25_PLUS] = TRANCHES;

/**
 * Calcule les 7 valeurs de référence.
 * @param consultations lignes RCE du mois (écran, file hors connexion incluse) — N'IMPORTE quelle forme brute : elles sont normalisées.
 * @param tpi doses TPI de la CPN du mois (null/absent = 0).
 *
 * Règles (alignées sur la Partie 3 du RMA, sans la fusionner avec elle) :
 *  - TDR : cas de fièvre « Oui » avec un résultat de TDR renseigné (tous âges, même sans âge exploitable) ;
 *  - ACT par tranche : TDR positif (101/110/111) ET traitement contenant « ACT », dans la tranche d'âge ;
 *  - Artésunate injectable : TDR positif ET traitement contenant « Artesunate injectable » (tous âges) ;
 *  - SP : TPI1 + TPI2 + TPI3 + TPI4 et plus.
 * Un âge illisible n'est jamais rangé dans une tranche par défaut (la ligne n'est simplement pas comptée dans les ACT par âge).
 */
export function calculerReferencesPalu(
  consultations: ReadonlyArray<Partial<Record<keyof ConsultationRow, unknown>> | null | undefined> | null | undefined,
  tpi?: Partial<TpiCpn> | null,
): ReferencesPalu {
  const lignes = normaliserLignes(consultations);
  const positifs = RULES.tdr.positifs as readonly string[];
  const estPositif = (l: ConsultationRow) => l.tdrResultat !== null && positifs.includes(l.tdrResultat);
  const traiteAct = (l: ConsultationRow) => !!l.traitementPalu?.includes(RULES.traitement.motCleAct);
  const traiteArtesunate = (l: ConsultationRow) => !!l.traitementPalu?.includes(RULES.traitement.motCleArtesunate);

  const r: ReferencesPalu = { ...REFERENCES_PALU_VIDES };
  for (const l of lignes) {
    if (l.casFievre === 'Oui' && l.tdrResultat) r.tdr += 1;
    if (!estPositif(l)) continue;
    if (traiteArtesunate(l)) r.artesunateInjectable += 1;
    if (!traiteAct(l)) continue;
    const tranche = tranchePaluSure(l.ageJours);
    if (tranche === T_0_11) r.act0_11mois += 1;
    else if (tranche === T_1_5) r.act1_5ans += 1;
    else if (tranche === T_6_13) r.act6_13ans += 1;
    else if (tranche === T_14_24 || tranche === T_25_PLUS) r.act14ansEtPlus += 1;
  }
  r.sulfadoxinePyrimethamine = entier(tpi?.tpi1) + entier(tpi?.tpi2) + entier(tpi?.tpi3) + entier(tpi?.tpi4EtPlus);
  return r;
}

/** Cas de paludisme grave (TDR+ traités par Artesunate injectable) par tranche d'âge — détail affiché sous la référence « Artesunate injectable ». */
export interface ArtesunateParTranche { m0_11: number; a1_5: number; a6_13: number; a14_24: number; a25Plus: number }
export const ARTESUNATE_PAR_TRANCHE_VIDE: ArtesunateParTranche = { m0_11: 0, a1_5: 0, a6_13: 0, a14_24: 0, a25Plus: 0 };

export function calculerArtesunateParTranche(
  consultations: ReadonlyArray<Partial<Record<keyof ConsultationRow, unknown>> | null | undefined> | null | undefined,
): ArtesunateParTranche {
  const positifs = RULES.tdr.positifs as readonly string[];
  const r: ArtesunateParTranche = { ...ARTESUNATE_PAR_TRANCHE_VIDE };
  for (const l of normaliserLignes(consultations)) {
    if (l.tdrResultat === null || !positifs.includes(l.tdrResultat)) continue;
    if (!l.traitementPalu?.includes(RULES.traitement.motCleArtesunate)) continue;
    const t = tranchePaluSure(l.ageJours); // âge illisible : pas de tranche par défaut
    if (t === T_0_11) r.m0_11 += 1;
    else if (t === T_1_5) r.a1_5 += 1;
    else if (t === T_6_13) r.a6_13 += 1;
    else if (t === T_14_24) r.a14_24 += 1;
    else if (t === T_25_PLUS) r.a25Plus += 1;
  }
  return r;
}
